import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { api, ApiError, storeToken, storedToken } from './api/client'
import type { User } from './api/types'

interface AuthState {
  user: User | null
  /** True until the stored token has been checked against the server. */
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, displayName: string) => Promise<void>
  signOut: () => void
  /** Writes the profile and keeps the cached user in step, so the header updates too. */
  saveProfile: (profile: Parameters<typeof api.updateProfile>[0]) => Promise<void>
  /** Deletes the account, then clears the local session. Irreversible. */
  deleteAccount: (password: string) => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  // A token in storage may have expired while the tab was closed, so it is confirmed against
  // /api/me before the app renders anything that assumes a signed-in user.
  useEffect(() => {
    let cancelled = false
    if (!storedToken()) {
      setLoading(false)
      return
    }
    api
      .me()
      .then((me) => {
        if (!cancelled) setUser(me)
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.isUnauthorised) {
          storeToken(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const response = await api.login(email, password)
    storeToken(response.token)
    setUser(response.user)
  }, [])

  const register = useCallback(async (email: string, password: string, displayName: string) => {
    const response = await api.register(email, password, displayName)
    storeToken(response.token)
    setUser(response.user)
  }, [])

  const saveProfile = useCallback(
    async (profile: Parameters<typeof api.updateProfile>[0]) => {
      setUser(await api.updateProfile(profile))
    },
    [],
  )

  const deleteAccount = useCallback(async (password: string) => {
    await api.deleteAccount(password)
    // Only after the server confirms: a failed delete must leave the session intact so the
    // error can be read and the password tried again.
    storeToken(null)
    setUser(null)
  }, [])

  const signOut = useCallback(() => {
    // Ask the server to revoke the token first — best effort, because signing out locally must
    // work even when offline or when the token has already expired.
    void api.logout().catch(() => {})
    storeToken(null)
    setUser(null)
  }, [])

  const value = useMemo<AuthState>(
    () => ({ user, loading, signIn, register, signOut, saveProfile, deleteAccount }),
    [user, loading, signIn, register, signOut, saveProfile, deleteAccount],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider')
  }
  return context
}
